import React, { useEffect, useState } from 'react';
import { api, type AdmissionProfileFields as Fields } from '../api';
import { TYPE, useTheme } from '../theme';
import { Button, Field, Input, Switch } from '../components/ui';

/**
 * P2: uppgifter som jaktmarker kontrollerar när du köper en jakt som kräver antagning
 * (examensår, hund, eftersök, skjutprov och jaktförsäkring). Sparas för sig, utan resten av profilen.
 */
export function AdmissionProfileFields() {
  const T = useTheme();
  const [f, setF] = useState<Fields | null>(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.getAdmissionProfile().then(setF).catch(() => setF(null)); }, []);
  if (!f) return null;
  const set = (patch: Partial<Fields>) => { setMsg(''); setF(v => ({ ...v!, ...patch })); };
  const save = async () => {
    setBusy(true);
    try {
      const clean = <T extends Record<string, any>>(o: T | null) => (o && Object.values(o).some(Boolean) ? o : null);
      await api.saveAdmissionProfile({ ...f, hunter_since_year: f.hunter_since_year ? Number(f.hunter_since_year) : null, shooting_test: clean(f.shooting_test), insurance: clean(f.insurance) });
      setMsg('Sparat');
    } catch (e: any) { setMsg(e.message); }
    setBusy(false);
  };
  const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 };
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: 18, marginBottom: 20 }} data-testid="admission-profile-fields">
      <div style={{ color: T.text, fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Uppgifter för antagning</div>
      <div style={{ ...TYPE.small, color: T.textSub, marginBottom: 14 }}>Jaktmarker som kräver antagning ser de här uppgifterna och jämför dem med sina krav när du köper en jakt.</div>
      <div style={grid}>
        <Field label="År för jägarexamen">
          <Input type="number" min={1940} max={2100} value={f.hunter_since_year ?? ''} onChange={e => set({ hunter_since_year: e.target.value ? Number(e.target.value) : null })} />
        </Field>
        <Field label="Skjutprov / skyttebevis">
          <Input value={f.shooting_test?.kind ?? ''} placeholder="T.ex. Björnpasset, Älgskyttemärket" onChange={e => set({ shooting_test: { ...f.shooting_test, kind: e.target.value } })} />
        </Field>
        <Field label="Skjutprovet gäller till">
          <Input type="date" value={f.shooting_test?.validUntil ?? ''} onChange={e => set({ shooting_test: { ...f.shooting_test, validUntil: e.target.value || null } })} />
        </Field>
        <Field label="Jaktförsäkring">
          <Input value={f.insurance?.provider ?? ''} placeholder="T.ex. via Svenska Jägareförbundet" onChange={e => set({ insurance: { ...f.insurance, provider: e.target.value } })} />
        </Field>
        <Field label="Försäkringen gäller till">
          <Input type="date" value={f.insurance?.validUntil ?? ''} onChange={e => set({ insurance: { ...f.insurance, validUntil: e.target.value || null } })} />
        </Field>
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8 }}>
        <Switch checked={f.dog_handler} onChange={v => set({ dog_handler: v })} label="Hundförare" style={{ color: T.text }} />
        <Switch checked={f.afterhunt_certified} onChange={v => set({ afterhunt_certified: v })} label="Eftersöksutbildad" style={{ color: T.text }} />
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 10 }}>
        <Button loading={busy} onClick={save}>Spara</Button>
        {msg && <span role="status" style={{ ...TYPE.small, color: msg === 'Sparat' ? T.ok : T.danger }}>{msg}</span>}
      </div>
    </div>
  );
}
